import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, AlertCircle, Loader2, Mail, ArrowRight, LogOut } from "lucide-react";

// Dual-mode page:
//   ?token=xxx  → POST /auth/verify-email and show success/error
//   (no token)  → "please verify your email" gate for unverified logged-in users
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, refresh, logout, resendVerification } = useAuth();
  const token = params.get("token");

  // gate mode local state
  const [resending, setResending] = useState(false);
  const [resentMsg, setResentMsg] = useState("");

  // token-mode local state
  const [state, setState] = useState(token ? "loading" : "gate");
  const [err, setErr] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        await refresh?.();
        setState(data.already_verified ? "already" : "ok");
      } catch (e) {
        setState("error");
        setErr(e?.response?.data?.detail || "Verification failed");
      }
    })();
  }, [token, refresh]);

  // Track whether we ever observed an unverified state on this mount, so we
  // can show a celebratory toast only when the flip happens live (and not
  // when a freshly-verified user lands here briefly during a route change).
  const wasUnverified = useRef(false);

  // Redirect verified users away from the gate.
  useEffect(() => {
    if (token) return;
    if (!user || user === false) return;
    if (!user.email_verified) {
      wasUnverified.current = true;
      return;
    }
    // Verified — fire toast only when this is a live flip from unverified.
    if (wasUnverified.current) {
      toast.success("Email verified — welcome aboard 👋", {
        description: "Loading your dashboard…",
        duration: 3000,
      });
    }
    nav("/dashboard", { replace: true });
  }, [user, token, nav]);

  // Poll /auth/me every 4s while sitting on the gate page so that when the
  // user clicks the verification link on another device (e.g. phone), the
  // desktop tab auto-flips to dashboard without a manual refresh. Stops once
  // user is verified or component unmounts.
  useEffect(() => {
    if (token) return; // token-mode handles its own state
    if (user && user !== false && user.email_verified) return; // already verified
    const id = setInterval(() => {
      refresh?.();
    }, 4000);
    return () => clearInterval(id);
  }, [token, user, refresh]);

  // "Create another account" — log out the current unverified user first so
  // AuthGate on /signup doesn't bounce them straight back to /verify-email.
  const switchAccount = async () => {
    try {
      await logout?.();
    } catch {
      // ignore — we'll navigate anyway
    }
    nav("/signup", { replace: true });
  };

  const onResend = async () => {
    setResending(true);
    setResentMsg("");
    try {
      await resendVerification();
      setResentMsg("Sent! Check your inbox.");
    } catch (e) {
      setResentMsg(e?.response?.data?.detail || "Could not resend — try again in a minute.");
    } finally {
      setResending(false);
    }
  };

  // === GATE MODE === (no token in URL)
  if (!token) {
    const reclaimed = params.get("reclaimed") === "1";
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-zinc-100 flex items-center justify-center p-6">
        <div className="max-w-sm w-full space-y-5" data-testid="verify-required">
          <Mail className="text-[#00E5FF]" size={36} />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono">
            {reclaimed ? "welcome back" : "verification required"}
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight" data-testid="verify-required-heading">
            {reclaimed ? "Welcome back — verify your email." : "Please verify your email."}
          </h1>
          <p className="text-zinc-400 text-sm leading-relaxed">
            {reclaimed ? (
              <>You already had a pending account, so we just resent a fresh verification link to{" "}
              <span className="text-zinc-100 font-mono break-all">{user?.email || "your email"}</span>.
              Click it to finish setting things up — the link expires in 24 hours.</>
            ) : (
              <>We sent a verification link to{" "}
              <span className="text-zinc-100 font-mono break-all">{user?.email || "your email"}</span>.
              Click it to unlock the dashboard and labs. The link expires in 24 hours.</>
            )}
          </p>
          <button
            onClick={onResend}
            disabled={resending}
            className="w-full py-2.5 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="verify-required-resend"
          >
            {resending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            {resending ? "sending…" : "resend verification email"}
          </button>
          {resentMsg && (
            <div className="text-xs text-zinc-400 font-mono" data-testid="verify-required-msg">
              {resentMsg}
            </div>
          )}
          <button
            onClick={logout}
            className="w-full py-2 text-xs text-zinc-500 hover:text-zinc-200 font-mono uppercase tracking-widest flex items-center justify-center gap-1.5"
            data-testid="verify-required-logout"
          >
            <LogOut size={11} /> sign out
          </button>
          <div className="text-[11px] text-zinc-600 font-mono">
            Used the wrong email?{" "}
            <button
              type="button"
              onClick={switchAccount}
              className="text-[#00E5FF] hover:underline cursor-pointer bg-transparent border-0 p-0 font-mono text-[11px]"
              data-testid="verify-required-switch-account"
            >
              create another account
            </button>
            .
          </div>
        </div>
      </div>
    );
  }

  // === TOKEN MODE === (?token=xxx)
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100 flex items-center justify-center p-6">
      <div className="max-w-sm w-full text-center space-y-5" data-testid="verify-email-page">
        {state === "loading" && (
          <>
            <Loader2 className="mx-auto text-[#00E5FF] animate-spin" size={32} />
            <div className="font-mono text-sm text-zinc-400">confirming your email…</div>
          </>
        )}
        {(state === "ok" || state === "already") && (
          <>
            <CheckCircle2 className="mx-auto text-[#00FF41]" size={42} />
            <h1 className="font-display font-black text-2xl" data-testid="verify-email-success">
              {state === "ok" ? "Email confirmed!" : "Already verified"}
            </h1>
            <p className="text-zinc-400 text-sm font-mono">
              {state === "ok"
                ? "Welcome aboard. You can now start labs."
                : "This email is already confirmed — feel free to log in."}
            </p>
            <button
              onClick={() => nav("/dashboard")}
              className="px-5 py-2.5 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center gap-2 mx-auto"
              data-testid="verify-email-continue"
            >
              go to dashboard <ArrowRight size={14} />
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="mx-auto text-[#EF4444]" size={32} />
            <h1 className="font-display font-black text-2xl">Verification failed</h1>
            <div className="text-sm font-mono text-[#EF4444]" data-testid="verify-email-error">
              {err}
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-[#00E5FF] text-zinc-200 hover:text-[#00E5FF] font-mono text-xs uppercase tracking-widest transition-colors"
            >
              <Mail size={12} /> back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
